/**
 * Admin Security Console for Eqaya Platform
 * Phase 2: Comprehensive Security Management Interface
 * 
 * Provides administrators with complete oversight, intervention capabilities,
 * and security management tools for the platform
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Badge,
  Tooltip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  AvatarGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Security,
  Warning,
  Error,
  Visibility,
  VisibilityOff,
  Refresh,
  FilterList,
  TrendingUp,
  TrendingDown,
  Timeline,
  Chat,
  People,
  Flag,
  CheckCircle,
  Cancel,
  Block,
  Speed,
  Analytics,
  Notifications,
  Settings,
  Download,
  Search,
  MoreVert,
  PlayArrow,
  Pause,
  Stop,
  Shield,
  Lock,
  VpnKey,
  AdminPanelSettings,
  SupervisorAccount,
  AssignmentInd,
  Report,
  History,
  Assessment,
  Build,
  NotificationImportant,
  ExpandMore,
  AccountCircle,
  Message,
  Phone,
  Email,
  Psychology,
  AutoGraph,
  SmartToy
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';

const AdminSecurityConsole = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [securityData, setSecurityData] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [interventionDialog, setInterventionDialog] = useState(false);
  const [userDetailsDialog, setUserDetailsDialog] = useState(false);
  const [securitySettingsDialog, setSecuritySettingsDialog] = useState(false);
  
  // Filter states
  const [timeRange, setTimeRange] = useState('24h');
  const [severity, setSeverity] = useState('all');
  const [status, setStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const wsRef = useRef(null);

  // Mock security data
  const [mockSecurityData] = useState({
    overview: {
      totalUsers: 1247,
      activeConversations: 89,
      violationsToday: 23,
      interventionsToday: 8,
      averageResponseTime: '2.3min',
      systemHealth: 98.5
    },
    realTimeAlerts: [
      {
        id: 'alert_001',
        type: 'behavioral_anomaly',
        severity: 'high',
        user: { id: 'user_123', name: 'John Doe', riskLevel: 'high' },
        message: 'Unusual messaging patterns detected - 15 messages in 2 minutes',
        timestamp: Date.now() - 2 * 60 * 1000,
        status: 'active',
        autoGenerated: true
      },
      {
        id: 'alert_002',
        type: 'coordinated_attempt',
        severity: 'critical',
        user: { id: 'user_456', name: 'Sarah Smith', riskLevel: 'critical' },
        message: 'Multiple circumvention attempts across different conversations',
        timestamp: Date.now() - 5 * 60 * 1000,
        status: 'investigating',
        autoGenerated: false
      },
      {
        id: 'alert_003',
        type: 'content_violation',
        severity: 'medium',
        user: { id: 'user_789', name: 'Mike Johnson', riskLevel: 'medium' },
        message: 'Phone number sharing detected in conversation',
        timestamp: Date.now() - 10 * 60 * 1000,
        status: 'resolved',
        autoGenerated: true
      }
    ],
    userRiskProfiles: [
      {
        id: 'user_123',
        name: 'John Doe',
        email: 'john@example.com',
        riskLevel: 'high',
        behaviorProfile: 'impatient',
        violationCount: 8,
        lastViolation: '2 hours ago',
        accountAge: 45,
        projectsCompleted: 2,
        currentRestrictions: ['enhanced_monitoring'],
        riskFactors: ['rapid_messaging', 'urgency_language', 'repeat_violations'],
        interventionHistory: [
          { type: 'warning', date: '2024-01-15', result: 'acknowledged' },
          { type: 'educational_content', date: '2024-01-14', result: 'viewed' }
        ]
      },
      {
        id: 'user_456',
        name: 'Sarah Smith',
        email: 'sarah@example.com',
        riskLevel: 'critical',
        behaviorProfile: 'manipulative',
        violationCount: 15,
        lastViolation: '30 minutes ago',
        accountAge: 12,
        projectsCompleted: 0,
        currentRestrictions: ['messaging_restricted', 'enhanced_monitoring'],
        riskFactors: ['coordinated_attempts', 'pattern_sophistication', 'persistence'],
        interventionHistory: [
          { type: 'account_restriction', date: '2024-01-15', result: 'active' },
          { type: 'manual_review', date: '2024-01-14', result: 'escalated' }
        ]
      }
    ],
    systemMetrics: {
      contentFiltering: {
        totalMessages: 15420,
        filteredMessages: 312,
        filterAccuracy: 97.8,
        falsePositives: 7,
        falseNegatives: 3
      },
      userBehavior: {
        behaviorProfilesGenerated: 1247,
        anomaliesDetected: 89,
        predictiveAccuracy: 84.2,
        interventionSuccessRate: 78.5
      },
      conversationMonitoring: {
        activeMonitors: 89,
        alertsGenerated: 156,
        escalationsToday: 12,
        averageResolutionTime: '18 minutes'
      }
    },
    trends: {
      violationsOverTime: [
        { time: '00:00', violations: 2, interventions: 1 },
        { time: '04:00', violations: 1, interventions: 0 },
        { time: '08:00', violations: 8, interventions: 3 },
        { time: '12:00', violations: 15, interventions: 5 },
        { time: '16:00', violations: 12, interventions: 4 },
        { time: '20:00', violations: 6, interventions: 2 }
      ],
      riskLevelDistribution: [
        { name: 'Low', value: 85, color: '#4caf50' },
        { name: 'Medium', value: 8, color: '#ff9800' },
        { name: 'High', value: 5, color: '#f44336' },
        { name: 'Critical', value: 2, color: '#d32f2f' }
      ],
      behaviorProfiles: [
        { name: 'Trustworthy', value: 70, color: '#4caf50' },
        { name: 'Cautious', value: 15, color: '#2196f3' },
        { name: 'Eager', value: 8, color: '#ff9800' },
        { name: 'Impatient', value: 4, color: '#f44336' },
        { name: 'Manipulative', value: 2, color: '#9c27b0' },
        { name: 'Hostile', value: 1, color: '#d32f2f' }
      ]
    }
  });

  useEffect(() => {
    loadSecurityData();
    
    if (realTimeEnabled) {
      startRealTimeUpdates();
    }

    return () => {
      stopRealTimeUpdates();
    };
  }, [realTimeEnabled, timeRange]);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Fetch real security data from API
      const [dashboardRes, alertsRes, riskProfilesRes, behavioralRes] = await Promise.all([
        api.get(`/admin/security/dashboard?timeRange=${timeRange}`),
        api.get('/admin/security/alerts?limit=100'),
        api.get('/admin/security/risk-profiles?minScore=0&limit=100'),
        api.get('/admin/security/behavioral-analysis')
      ]);

      const dashboardData = dashboardRes.data;
      const alertsData = alertsRes.data;
      const riskProfilesData = riskProfilesRes.data;
      const behavioralData = behavioralRes.data;

      if (dashboardData.success) {
        // Transform API data to match component structure
        const transformedData = {
          overview: {
            totalUsers: dashboardData.data.riskUsers?.length || 0,
            activeConversations: dashboardData.data.summary.activeMonitoring || 0,
            violationsToday: dashboardData.data.summary.totalViolations || 0,
            interventionsToday: 0, // Can be calculated from interventions
            highRiskAlerts: alertsData.data?.alerts?.filter(a => a.severity === 'high' || a.severity === 'critical').length || 0,
            automatedBlocks: 0
          },
          alerts: alertsData.data?.alerts || [],
          riskProfiles: riskProfilesData.data?.profiles || [],
          behavioralAnalysis: behavioralData.data?.analyses || [],
          violations: dashboardData.data.violations || []
        };
        setSecurityData(transformedData);
      } else {
        // Fallback to mock data if API fails
        setSecurityData(mockSecurityData);
      }
    } catch (error) {
      console.error('Failed to load security data:', error);
      // Fallback to mock data on error
      setSecurityData(mockSecurityData);
    } finally {
      setLoading(false);
    }
  };

  const startRealTimeUpdates = () => {
    // WebSocket connection for real-time security updates
    wsRef.current = new WebSocket('ws://localhost:5001/admin/security');
    
    wsRef.current.onmessage = (event) => {
      const update = JSON.parse(event.data);
      handleSecurityUpdate(update);
    };
  };

  const stopRealTimeUpdates = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  const handleSecurityUpdate = (update) => {
    setSecurityData(prev => ({
      ...prev,
      ...update
    }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[severity] || 'default';
  };

  const getRiskLevelColor = (riskLevel) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      critical: '#d32f2f'
    };
    return colors[riskLevel] || '#666';
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleUserIntervention = async (userId, interventionType) => {
    try {
      console.log(`Applying ${interventionType} to user ${userId}`);
      
      // Update local state
      setSecurityData(prev => ({
        ...prev,
        userRiskProfiles: prev.userRiskProfiles.map(user =>
          user.id === userId
            ? { ...user, currentRestrictions: [...user.currentRestrictions, interventionType] }
            : user
        )
      }));
      
      setInterventionDialog(false);
    } catch (error) {
      console.error('Intervention failed:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading security console...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <Shield sx={{ mr: 2, verticalAlign: 'middle' }} />
          Security Administration Console
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            size="small"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="1h">Last Hour</MenuItem>
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last Week</MenuItem>
            <MenuItem value="30d">Last Month</MenuItem>
          </Select>
          
          <FormControlLabel
            control={
              <Switch
                checked={realTimeEnabled}
                onChange={(e) => setRealTimeEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Live Updates"
          />
          
          <Button
            variant="outlined"
            startIcon={<Settings />}
            onClick={() => setSecuritySettingsDialog(true)}
          >
            Settings
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSecurityData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* System Health Alert */}
      <Alert 
        severity={securityData.overview.systemHealth > 95 ? "success" : "warning"} 
        sx={{ mb: 3 }}
        icon={<Shield />}
      >
        <Typography variant="body1">
          System Health: {securityData.overview.systemHealth}% | 
          Active Monitors: {securityData.overview.activeConversations} | 
          Response Time: {securityData.overview.averageResponseTime}
          {realTimeEnabled && <Chip label="LIVE" color="success" size="small" sx={{ ml: 2 }} />}
        </Typography>
      </Alert>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {securityData.overview.totalUsers.toLocaleString()}
                  </Typography>
                </Box>
                <People color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Conversations
                  </Typography>
                  <Typography variant="h4">
                    {securityData.overview.activeConversations}
                  </Typography>
                </Box>
                <Chat color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Violations Today
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {securityData.overview.violationsToday}
                  </Typography>
                </Box>
                <Warning color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Interventions Today
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {securityData.overview.interventionsToday}
                  </Typography>
                </Box>
                <AdminPanelSettings color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    System Health
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {securityData.overview.systemHealth}%
                  </Typography>
                </Box>
                <Assessment color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Console Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Live Alerts" icon={<NotificationImportant />} iconPosition="start" />
            <Tab label="User Risk Profiles" icon={<Psychology />} iconPosition="start" />
            <Tab label="System Analytics" icon={<Analytics />} iconPosition="start" />
            <Tab label="Behavior Analysis" icon={<SmartToy />} iconPosition="start" />
            <Tab label="Intervention Tools" icon={<Build />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Live Alerts Tab */}
        {activeTab === 0 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Real-Time Security Alerts</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Select
                  size="small"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  <MenuItem value="all">All Severities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
                <TextField
                  size="small"
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <Search /> }}
                />
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Severity</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {securityData.realTimeAlerts
                    .filter(alert => severity === 'all' || alert.severity === severity)
                    .map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>
                          <Chip
                            size="small"
                            color={getSeverityColor(alert.severity)}
                            label={alert.severity.toUpperCase()}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {alert.autoGenerated && <SmartToy fontSize="small" />}
                            {alert.type.replace('_', ' ').toUpperCase()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box 
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedUser(alert.user);
                              setUserDetailsDialog(true);
                            }}
                          >
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {alert.user.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{alert.user.name}</Typography>
                              <Chip
                                size="small"
                                label={alert.user.riskLevel}
                                sx={{ 
                                  backgroundColor: getRiskLevelColor(alert.user.riskLevel),
                                  color: 'white',
                                  fontSize: '0.7rem'
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{alert.message}</TableCell>
                        <TableCell>{formatTimeAgo(alert.timestamp)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={alert.status === 'active' ? 'error' : alert.status === 'investigating' ? 'warning' : 'success'}
                            label={alert.status.toUpperCase()}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setSelectedUser(alert.user);
                              setInterventionDialog(true);
                            }}
                          >
                            Intervene
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        {/* User Risk Profiles Tab */}
        {activeTab === 1 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>High-Risk User Profiles</Typography>
            
            <Grid container spacing={3}>
              {securityData.userRiskProfiles.map((user) => (
                <Grid item xs={12} md={6} key={user.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderLeft: `4px solid ${getRiskLevelColor(user.riskLevel)}`,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedUser(user);
                      setUserDetailsDialog(true);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar>{user.name.charAt(0)}</Avatar>
                          <Box>
                            <Typography variant="h6">{user.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                          <Chip
                            label={`${user.riskLevel.toUpperCase()} RISK`}
                            sx={{
                              backgroundColor: getRiskLevelColor(user.riskLevel),
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          <Chip
                            size="small"
                            label={user.behaviorProfile}
                            variant="outlined"
                          />
                        </Box>
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Violations</Typography>
                          <Typography variant="h6" color="error">{user.violationCount}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Projects</Typography>
                          <Typography variant="h6">{user.projectsCompleted}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">Account Age</Typography>
                          <Typography variant="h6">{user.accountAge}d</Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Current Restrictions:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.currentRestrictions.map((restriction, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={restriction.replace('_', ' ')}
                              color="warning"
                            />
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Risk Factors:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.riskFactors.map((factor, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={factor.replace('_', ' ')}
                              color="error"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Last violation: {user.lastViolation}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        )}

        {/* System Analytics Tab */}
        {activeTab === 2 && (
          <CardContent>
            <Grid container spacing={3}>
              {/* Performance Metrics */}
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  Security Violations & Interventions Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={securityData.trends.violationsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="violations" stroke="#f44336" strokeWidth={2} name="Violations" />
                    <Line type="monotone" dataKey="interventions" stroke="#2196f3" strokeWidth={2} name="Interventions" />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  User Risk Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={securityData.trends.riskLevelDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {securityData.trends.riskLevelDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              {/* System Performance Cards */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Content Filtering</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Messages Processed</Typography>
                      <Typography variant="h4">{securityData.systemMetrics.contentFiltering.totalMessages.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Filter Accuracy</Typography>
                      <Typography variant="h4" color="success.main">
                        {securityData.systemMetrics.contentFiltering.filterAccuracy}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={securityData.systemMetrics.contentFiltering.filterAccuracy} 
                      color="success"
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Behavior Analysis</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Profiles Generated</Typography>
                      <Typography variant="h4">{securityData.systemMetrics.userBehavior.behaviorProfilesGenerated.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Predictive Accuracy</Typography>
                      <Typography variant="h4" color="info.main">
                        {securityData.systemMetrics.userBehavior.predictiveAccuracy}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={securityData.systemMetrics.userBehavior.predictiveAccuracy} 
                      color="info"
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Intervention Success</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Success Rate</Typography>
                      <Typography variant="h4" color="warning.main">
                        {securityData.systemMetrics.userBehavior.interventionSuccessRate}%
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">Avg Resolution Time</Typography>
                      <Typography variant="h6">
                        {securityData.systemMetrics.conversationMonitoring.averageResolutionTime}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={securityData.systemMetrics.userBehavior.interventionSuccessRate} 
                      color="warning"
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* Behavior Analysis Tab */}
        {activeTab === 3 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>Behavioral Pattern Analysis</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  User Behavior Profile Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={securityData.trends.behaviorProfiles}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip />
                    <Bar dataKey="value" fill="#2196f3" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Behavior Profile Breakdown
                </Typography>
                <List>
                  {securityData.trends.behaviorProfiles.map((profile, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: profile.color
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={profile.name}
                        secondary={`${profile.value}% of users`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Pattern Detection Insights</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              AI-powered behavioral analysis provides predictive insights for early intervention.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      <TrendingUp sx={{ mr: 1 }} />
                      Escalation Patterns
                    </Typography>
                    <Typography variant="body2">
                      Detected unusual escalation in 12 conversations where users became increasingly
                      urgent about sharing contact information. Recommend proactive education.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="error.main" gutterBottom>
                      <Psychology sx={{ mr: 1 }} />
                      Manipulation Tactics
                    </Typography>
                    <Typography variant="body2">
                      Identified 5 users employing sophisticated manipulation techniques including
                      urgency creation and isolation tactics. Enhanced monitoring recommended.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* Intervention Tools Tab */}
        {activeTab === 4 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>Administrative Intervention Tools</Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>⚠️ Administrative Actions Required</strong><br/>
                Use these tools carefully. All actions are logged and auditable.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* Quick Actions */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Speed sx={{ mr: 1 }} />
                      Quick Interventions
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<NotificationImportant />}
                        fullWidth
                      >
                        Send Security Alert to All High-Risk Users
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Block />}
                        fullWidth
                      >
                        Temporarily Restrict All Critical Users
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="info"
                        startIcon={<Visibility />}
                        fullWidth
                      >
                        Enable Enhanced Monitoring for All
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* System Controls */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Settings sx={{ mr: 1 }} />
                      System Controls
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Automatic Content Filtering"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Real-time Behavior Analysis"
                      />
                      <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Predictive Risk Assessment"
                      />
                      <FormControlLabel
                        control={<Switch />}
                        label="Emergency Lockdown Mode"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Actions Log */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <History sx={{ mr: 1 }} />
                      Recent Administrative Actions
                    </Typography>
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Target</TableCell>
                            <TableCell>Administrator</TableCell>
                            <TableCell>Result</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>2 min ago</TableCell>
                            <TableCell>Enhanced Monitoring</TableCell>
                            <TableCell>user_123 (John Doe)</TableCell>
                            <TableCell>admin</TableCell>
                            <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>15 min ago</TableCell>
                            <TableCell>Account Restriction</TableCell>
                            <TableCell>user_456 (Sarah Smith)</TableCell>
                            <TableCell>admin</TableCell>
                            <TableCell><Chip label="Applied" color="warning" size="small" /></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>1 hour ago</TableCell>
                            <TableCell>Security Alert Sent</TableCell>
                            <TableCell>All High-Risk Users</TableCell>
                            <TableCell>admin</TableCell>
                            <TableCell><Chip label="Delivered" color="info" size="small" /></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsDialog}
        onClose={() => setUserDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setUserDetailsDialog(false)} />
        <DialogTitle>
          User Security Profile: {selectedUser?.name || 'N/A'}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Profile Information</Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon><AccountCircle /></ListItemIcon>
                      <ListItemText primary="Email" secondary={selectedUser.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Psychology /></ListItemIcon>
                      <ListItemText primary="Behavior Profile" secondary={selectedUser.behaviorProfile} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Warning /></ListItemIcon>
                      <ListItemText primary="Risk Level" secondary={selectedUser.riskLevel} />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Intervention History</Typography>
                  <Stepper orientation="vertical">
                    {selectedUser.interventionHistory.map((intervention, index) => (
                      <Step key={index} active={true}>
                        <StepLabel>{intervention.type.replace('_', ' ')}</StepLabel>
                        <StepContent>
                          <Typography variant="body2">
                            {intervention.date} - {intervention.result}
                          </Typography>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDetailsDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => {
              setUserDetailsDialog(false);
              setInterventionDialog(true);
            }}
          >
            Apply Intervention
          </Button>
        </DialogActions>
      </Dialog>

      {/* Intervention Dialog */}
      <Dialog
        open={interventionDialog}
        onClose={() => setInterventionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setInterventionDialog(false)} />
        <DialogTitle>Apply User Intervention</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Select intervention for user: <strong>{selectedUser?.name}</strong>
          </Typography>
          
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<Message />}
              onClick={() => handleUserIntervention(selectedUser?.id, 'send_warning')}
            >
              Send Security Warning Message
            </Button>
            
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Visibility />}
              onClick={() => handleUserIntervention(selectedUser?.id, 'enhanced_monitoring')}
            >
              Enable Enhanced Monitoring
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<Block />}
              onClick={() => handleUserIntervention(selectedUser?.id, 'restrict_messaging')}
            >
              Restrict Messaging Capabilities
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => handleUserIntervention(selectedUser?.id, 'suspend_account')}
            >
              Suspend Account (Temporary)
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Security Settings Dialog */}
      <Dialog
        open={securitySettingsDialog}
        onClose={() => setSecuritySettingsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setSecuritySettingsDialog(false)} />
        <DialogTitle>Security System Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Configure security monitoring and intervention parameters.
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Advanced security settings will be implemented in the next phase.
            This interface provides configuration for all security parameters.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecuritySettingsDialog(false)}>Close</Button>
          <Button variant="contained" color="primary">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSecurityConsole;